/* oxlint-disable @typescript-eslint/consistent-type-assertions */

import * as Ably from "ably";
import type { FC, PropsWithChildren } from "react";
import React, { useEffect, useRef } from "react";

import type { Promisable } from "@core/foundation/promise";
import { hasValue } from "@core/foundation/types";

import { useLogger } from "../../logger/index.ts";
import type {
  NotificationContextValue,
  NotificationListener,
} from "../contexts/notifications/context.ts";
import { NotificationContext } from "../contexts/notifications/context.ts";

interface NotificationSubscription {
  readonly channel: string;
  readonly notificationToken: unknown;
}

interface AblyNotificationProviderProps {
  readonly subscribe: () => Promisable<NotificationSubscription>;
}

export const AblyNotificationProvider: FC<
  PropsWithChildren<AblyNotificationProviderProps>
> = ({ children, subscribe }) => {
  const subscribeRef = useRef(subscribe);

  const listenersRef = useRef(new Set<NotificationListener>());

  const logger = useLogger();

  const value: NotificationContextValue = {
    addListener: listener => {
      listenersRef.current.add(listener);
    },
    removeListener: listener => {
      listenersRef.current.delete(listener);
    },
  };

  useEffect(() => {
    subscribeRef.current = subscribe;
  });

  useEffect(() => {
    let mounted = true;

    let client: Ably.Realtime | undefined;

    const connect = async (): Promise<void> => {
      const { channel, notificationToken: firstNotificationToken } =
        await subscribeRef.current();

      if (!mounted) {
        return;
      }

      let isFirstToken = true;
      const getNotificationToken = async (): Promise<unknown> => {
        if (isFirstToken) {
          isFirstToken = false;

          return firstNotificationToken;
        }

        const { notificationToken } = await subscribeRef.current();

        return notificationToken;
      };

      client = new Ably.Realtime({
        authCallback: (_tokenParams, callback) => {
          if (!mounted) {
            return;
          }

          getNotificationToken()
            .then(notificationToken => {
              if (!mounted) {
                return;
              }

              callback(null, notificationToken as Ably.TokenRequest);
            })
            .catch((error: unknown) => {
              if (!mounted) {
                return;
              }

              callback(
                error instanceof Error ? error.message : "Token fetch failed",
                null,
              );
            });
        },
      });

      await client.channels.get(channel).subscribe(msg => {
        if (!mounted) {
          return;
        }

        if (hasValue(msg.name)) {
          for (const listener of listenersRef.current) {
            listener(msg.name, msg.data);
          }
        }
      });
    };

    connect().catch((error: unknown) => {
      if (!mounted) {
        return;
      }

      logger.error("Notification channel subscription failed", error);
    });

    return () => {
      mounted = false;
      client?.close();
    };
  }, [logger]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
